/**
 * The TrueReel Agent (docs/03 §6): the daemon a user runs on their own GPU.
 * Pairing goes through the Accounts device grant; the API approves the code
 * the agent printed and lists the org's agents with their last heartbeat.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./client";
import type { AgentStatus, AgentVersion, PairResponse } from "./types";

export const AGENT_STATUS_KEY = ["agent", "status"] as const;
export const AGENT_VERSION_KEY = ["agent", "version"] as const;

/** How often the agent page re-reads `GET /v1/agent/status` while open, so a
 *  freshly paired machine appears without a reload. */
export const AGENT_STATUS_REFETCH_MS = 30_000;

export function useAgentStatus() {
  return useQuery({
    queryKey: AGENT_STATUS_KEY,
    queryFn: () => api<AgentStatus>("/v1/agent/status"),
    refetchInterval: AGENT_STATUS_REFETCH_MS,
  });
}

/** `GET /v1/agent/version`: the current agent release and where to get it.
 *  Public and static, so it is cached for the session. */
export function useAgentVersion() {
  return useQuery({
    queryKey: AGENT_VERSION_KEY,
    queryFn: () => api<AgentVersion>("/v1/agent/version"),
    staleTime: Infinity,
  });
}

export interface PairInput {
  /** The code the agent printed, e.g. `BCDF-2345`; the API normalises case
   *  and the hyphen. */
  user_code: string;
  /** What to call the machine, ≤ 100 chars. */
  name: string;
}

/** `POST /v1/agent/pair` → `{device_code_id, org_id, name}`. Errors:
 *  `code_not_found` (404), `code_expired` (400), `forbidden` (403: BYO not
 *  enabled, or the session cannot act on Accounts). */
export function usePairAgent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: PairInput) =>
      api<PairResponse>("/v1/agent/pair", { method: "POST", body }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: AGENT_STATUS_KEY });
    },
  });
}
